import { makeAutoObservable, runInAction } from "mobx";
import {
  CreateSurvey,
  HlaDefaultValues,
  Survey,
  SurveyDefaults,
  UpdateSurvey,
  UpdateSurveyDefaults,
} from "../models/Survey";
import { RootStore } from "../../shared/stores/RootStore";
import { SurveyApi } from "../services/SurveyApi";
import { compileAllPages } from "../../shared/utils/http-utils";
import { DistanceUnit } from "@keita/lanepatrol-sdk";
import {
  getDefaultAverageDailyTraffic,
  getDefaultAverageRoadSpeed,
  HlaWayType,
} from "../../hla/models/HlaWay";

interface MapSelection {
  x: number;
  y: number;
  surveys: Survey[];
}
export class SurveyStore {
  rootStore;
  surveys: Survey[] = [];
  selectedId?: string;

  distanceUnit: DistanceUnit = DistanceUnit.Metric;
  rightHandTraffic = true;

  showSurveyDefaultsModal = false;
  defaults?: SurveyDefaults;

  mapSelection: MapSelection | undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.surveys = [];
      this.selectedId = undefined;
    });
  }

  get speedUnitStr() {
    return this.distanceUnit === DistanceUnit.Imperial ? "mph" : "km/h";
  }

  get distanceUnitStr() {
    return this.distanceUnit === DistanceUnit.Imperial ? "mi" : "km";
  }

  private setSurveys(surveys: Survey[]) {
    runInAction(() => {
      this.surveys = surveys;
    });

    const { mapStore } = this.rootStore;
    mapStore.surveysMapSource?.eventSurveysUpdated();
  }

  get(surveyId?: string) {
    if (!surveyId) return;
    return this.surveys.find((survey) => survey.id === surveyId);
  }

  getSelected() {
    if (!this.selectedId) return;
    return this.get(this.selectedId);
  }

  select(surveyId?: string) {
    runInAction(() => (this.selectedId = surveyId));
    this.updateSurveySettings();
  }

  updateSurveySettings() {
    const survey = this.getSelected();
    if (survey) {
      runInAction(() => {
        this.distanceUnit = survey.distanceUnit;
        this.rightHandTraffic = survey.rightHandTraffic;
      });
    }
  }

  setMapSelection(x: number, y: number, surveyIds: string[]) {
    runInAction(() => {
      this.mapSelection = {
        x,
        y,
        surveys: this.surveys.filter((p) => surveyIds.includes(p.id)),
      };
    });
  }

  clearMapSelection() {
    runInAction(() => {
      this.mapSelection = undefined;
    });
  }

  async fetchSurveyDefaults(surveyId: string, cancelIf?: () => boolean) {
    const { data: defaults } = await SurveyApi.getDefaults(surveyId);
    if (cancelIf?.()) return;

    runInAction(() => {
      this.defaults = defaults;
    });
  }

  async addSurvey(newProj: CreateSurvey) {
    const response = await SurveyApi.create(newProj);
    await this.fetchAllSurveys();
    return response.data;
  }

  async fetchAllSurveys(cancelIf?: () => boolean) {
    const surveys = await compileAllPages(
      async (pageNumber) => await SurveyApi.page(20, pageNumber),
    );
    if (cancelIf?.()) return;

    this.setSurveys(surveys);
    this.updateSurveySettings();
  }

  async fetchSurvey(surveyId: string, cancelIf?: () => boolean) {
    const { data: survey } = await SurveyApi.get(surveyId);
    if (cancelIf?.()) return;

    if (this.surveys.find((s) => (s.id = surveyId))) {
      this.setSurveys(
        this.surveys.map((s) => (s.id === surveyId ? survey : s)),
      );
    } else {
      this.setSurveys([...this.surveys, survey]);
    }

    this.updateSurveySettings();
  }

  async updateSurvey(body: UpdateSurvey, id: string) {
    const response = await SurveyApi.update(body, id);
    this.setSurveys(this.surveys.map((p) => (p.id === id ? response.data : p)));
    this.updateSurveySettings();
  }

  async deleteSurvey(surveyId: string) {
    await SurveyApi.delete(surveyId);
    this.setSurveys(this.surveys.filter((p) => p.id !== surveyId));
  }

  async removeFromLibrary(surveyId: string) {
    await SurveyApi.deleteMyAccess(surveyId);
    this.setSurveys(this.surveys.filter((p) => p.id !== surveyId));
  }

  async updateDefaults(id: string, body: UpdateSurveyDefaults) {
    const response = await SurveyApi.updateDefaults(id, body);
    runInAction(() => {
      this.defaults = response.data;
    });
  }

  setShowSurveyDefaultsModal(value: boolean) {
    runInAction(() => {
      this.showSurveyDefaultsModal = value;
    });
  }

  getDefaultAverageRoadSpeed(hlaWayType: HlaWayType): number {
    const key = hlaWayType.toLowerCase() as keyof HlaDefaultValues;
    return (
      this.defaults?.averageRoadSpeed[key] ??
      getDefaultAverageRoadSpeed(hlaWayType, this.distanceUnit)
    );
  }

  getDefaultAverageDailyTraffic(hlaWayType: HlaWayType): number {
    const key = hlaWayType.toLowerCase() as keyof HlaDefaultValues;
    return (
      this.defaults?.averageDailyTraffic[key] ??
      getDefaultAverageDailyTraffic(hlaWayType)
    );
  }
}
